import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
	NETSALES,
	NIL_PICK_RATE,
	STORE_COUNT,
	SUMMARY_CARD_DATA_NOT_AVAILABLE,
	REPL_IN_STOCK,
} from '../../../constants/constant';
import { UserPortalContext } from '../../../context/appcontext';
import { useSummaryCardsStyles, summaryCardsStyles } from './styles';
import {
	getItemStoreCountSummaryCards,
	getItemNilPickRateSummaryCards,
	getItemNetSalesSummaryCards,
	getItemInStockSummaryCards,
} from '../../../redux/actions/itemDrillDownActions';

export const Card = ({ title, data, loading, errorCode, style }) => {
	const classes = useSummaryCardsStyles();
	const { tyValue, yoy, date } = data;

	const getDate = (date) => {
		const dateArray = date?.split('-');
		const monthName = Intl.DateTimeFormat('en', { month: 'short' }).format(
			new Date(dateArray[1])
		);
		return `${dateArray[2]}-${monthName}-${dateArray[0]}`;
	};

	let component = null;
	if (loading) {
		component = (
			<Box className={classes.loader}>
				<CircularProgress className={classes.circularProgress} />
			</Box>
		);
	} else if (!loading && errorCode === 204) {
		component = (
			<Box className={classes.noData}>
				<Typography style={summaryCardsStyles.title}>{title}</Typography>
				<Typography style={summaryCardsStyles.noDataContent}>
					{SUMMARY_CARD_DATA_NOT_AVAILABLE}
				</Typography>
			</Box>
		);
	} else {
		component = (
			<Fragment>
				<Typography style={summaryCardsStyles.title}>
					{!date ? (
						title
					) : (
						<>
							<span style={summaryCardsStyles.titleDate}>{title} - </span>
							<span style={{ fontSize: '14px' }}>{getDate(date)}</span>
						</>
					)}
				</Typography>
				<Typography style={summaryCardsStyles.value}>{tyValue}</Typography>
				<Typography
					style={
						yoy?.includes('+') ? summaryCardsStyles.up : summaryCardsStyles.down
					}>
					{yoy}
				</Typography>
			</Fragment>
		);
	}

	return (
		<Grid
			item
			xl={6}
			lg={3}
			md={3}
			sm={3}
			xs={6}
			className={classes.card}
			sx={summaryCardsStyles[style]}>
			{component}
		</Grid>
	);
};

Card.propTypes = {
	title: PropTypes.string,
	data: PropTypes.object,
	errorCode: PropTypes.number,
	loading: PropTypes.bool,
	style: PropTypes.string,
};

function ItemSummaryCards({
	weekFilter,
	category,
	selectedFilter,
	itemNum,
	startDate,
	endDate,
}) {
	const dispatch = useDispatch();
	const classes = useSummaryCardsStyles();
	const portalUserContext = React.useContext(UserPortalContext);
	const {
		netSalesSummaryCard,
		inStockSummaryCard,
		storeCountSummaryCard,
		nilPickRateSummaryCard,
	} = useSelector((state) => state.itemDrillDownReducer);

	const selectedView = useSelector(
		(state) => state.appGuideReducer.selectedView
	);

	const urlParams = new URLSearchParams(window.location.search);
	const subCatId = urlParams.get('subCatId');
	const brandId = urlParams.get('brandId');

	React.useEffect(() => {
		if (itemNum) {
			const params = {
				categoryId: '-1',
				period: weekFilter,
				selectedFilter,
				portalUserContext,
				itemNum,
				subCatId,
				brandId,
				startDate,
				endDate,
			};
			dispatch(getItemNetSalesSummaryCards(params));
			dispatch(getItemInStockSummaryCards(params));
			dispatch(getItemNilPickRateSummaryCards(params));
			dispatch(getItemStoreCountSummaryCards(params));
		}
	}, [
		weekFilter,
		category,
		itemNum,
		subCatId,
		brandId,
		startDate,
		endDate,
		selectedView,
	]);

	return (
		<Grid container padding={'6px'} className={classes.container}>
			{[
				[NETSALES, netSalesSummaryCard],
				[REPL_IN_STOCK, inStockSummaryCard],
				[STORE_COUNT, storeCountSummaryCard],
				[NIL_PICK_RATE, nilPickRateSummaryCard],
			]?.map((item, index) => {
				return (
					<Card
						key={index}
						title={item[0]}
						data={{
							tyValue: item[1]?.tyValue,
							yoy: item[1]?.yoy,
							date: item[1]?.bus_dt,
						}}
						errorCode={item[1]?.errorCode}
						loading={item[1]?.loading}
						style={index < 2 ? 'topCard' : 'bottomCard'}
					/>
				);
			})}
		</Grid>
	);
}

ItemSummaryCards.propTypes = {
	itemNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	category: PropTypes.string,
	selectedFilter: PropTypes.string,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
};

export default ItemSummaryCards;
